/**
 * Fill-in form PDF preview overlay — PDF view + admin field edit / round snapshot compare.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChatFlowFormFillRound, ChatFlowFormToFillItem } from '../../../../services/chatFlowApi';
import {
  extractEditableValues,
  extractSnapshotValues,
  fetchAdminFormSubmission,
  isAdminFormEditSupported,
  mergeEditableValues,
  updateAdminFormSubmission,
  type AdminFormSubmissionView,
} from '../../../../services/adminFormSubmissionApi';
import { resolveFillInPdfPreviewKind } from '../../../employee/fillingForms/fillInFormRoutes';
import {
  buildFillInPdfPreviewUrl,
  fetchFillInPdfPreviewBlob,
} from '../../../employee/documents/pdfPreview/fillInPdfPreviewModel';
import PdfCanvasViewer from '../../../employee/documents/pdfPreview/PdfCanvasViewer';
import downloadAllIcon from '../../../../assests/icons/downloadAll.png';
import approveIcon from '../../../../assests/icons/approved.png';
import rejectIcon from '../../../../assests/icons/rejected.png';
import { safeFileNamePart } from './utils';
import ResubmitCommentModal from './ResubmitCommentModal';
import { FormFieldEditor } from './FormFieldEditor';
import WorkerRegistryAdminFieldEditor from './WorkerRegistryAdminFieldEditor';
import HireHealthSurveyAdminFieldEditor from './HireHealthSurveyAdminFieldEditor';
import BankAccountDetailsAdminFieldEditor from './BankAccountDetailsAdminFieldEditor';
import SiteArrivalConfirmationAdminFieldEditor from './SiteArrivalConfirmationAdminFieldEditor';
import './DocumentPreview.css';
import './ProfileSectionPreview.css';

export interface FilledFormPreviewProps {
  form: ChatFlowFormToFillItem;
  activeRound: ChatFlowFormFillRound;
  employeeEmail: string;
  employeeName?: string;
  invitationId: string;
  allowResubmit?: boolean;
  readOnly?: boolean;
  onClose: () => void;
  showResubmitModal: boolean;
  resubmitReason: string;
  onResubmitReasonChange: (value: string) => void;
  onOpenResubmitModal: () => void;
  onCloseResubmitModal: () => void;
  onSaveResubmit: () => void;
  onApprove: () => void;
}

type ViewMode = 'view' | 'edit';
type PanelTab = 'pdf' | 'fields';

const FilledFormPreview: React.FC<FilledFormPreviewProps> = ({
  form,
  activeRound,
  employeeEmail,
  employeeName,
  invitationId,
  allowResubmit = true,
  readOnly = false,
  onClose,
  showResubmitModal,
  resubmitReason,
  onResubmitReasonChange,
  onOpenResubmitModal,
  onCloseResubmitModal,
  onSaveResubmit,
  onApprove,
}) => {
  const [reverseConfirm, setReverseConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(false);
  const [pdfRefreshKey, setPdfRefreshKey] = useState(0);

  const [adminLoadState, setAdminLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [adminView, setAdminView] = useState<AdminFormSubmissionView | null>(null);
  const [mode, setMode] = useState<ViewMode>('view');
  const [panelTab, setPanelTab] = useState<PanelTab>('pdf');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const formTitle = form.nameJa ?? form.name;
  const canAdminEdit = !readOnly && allowResubmit && isAdminFormEditSupported(form.formCode);
  const reviewDecision: boolean | null =
    activeRound.adminApproved === true ? true : activeRound.adminApproved === false ? false : null;
  const activeAdminComment = activeRound.adminComment ?? null;

  const previewApiUrl = useMemo(() => {
    const kind = resolveFillInPdfPreviewKind(form.formCode);
    if (!kind || !employeeEmail?.trim()) return null;
    return buildFillInPdfPreviewUrl({
      kind,
      documentTitle: formTitle,
      formCode: form.formCode,
      email: employeeEmail.trim(),
      invitationId: invitationId.trim() || undefined,
      taxYear: new Date().getFullYear(),
    });
  }, [form.formCode, formTitle, employeeEmail, invitationId]);

  const loadAdminData = useCallback(async () => {
    if (!employeeEmail.trim() || !invitationId.trim()) {
      setAdminLoadState('error');
      return;
    }
    setAdminLoadState('loading');
    try {
      const data = await fetchAdminFormSubmission(
        form.formCode,
        employeeEmail.trim(),
        invitationId.trim(),
        activeRound.submissionRound
      );
      setAdminView(data);
      if (!allowResubmit && data.snapshot) {
        setFieldValues(extractSnapshotValues(data.snapshot));
      } else {
        setFieldValues(extractEditableValues(data.live));
      }
      setAdminLoadState('ready');
    } catch {
      setAdminLoadState('error');
    }
  }, [employeeEmail, invitationId, form.formCode, activeRound.submissionRound, allowResubmit]);

  useEffect(() => {
    void loadAdminData();
  }, [loadAdminData]);

  useEffect(() => {
    const kind = resolveFillInPdfPreviewKind(form.formCode);
    if (!kind || !employeeEmail?.trim()) {
      setPreviewError(true);
      setPreviewLoading(false);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(false);
    setPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    let cancelled = false;
    void fetchFillInPdfPreviewBlob({
      kind,
      documentTitle: formTitle,
      formCode: form.formCode,
      email: employeeEmail.trim(),
      invitationId: invitationId.trim() || undefined,
      taxYear: new Date().getFullYear(),
    })
      .then((blob) => {
        if (cancelled) return;
        setPreviewBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!cancelled) setPreviewError(true);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.formCode, formTitle, employeeEmail, invitationId, pdfRefreshKey]);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);

  const snapshotFieldValues = useMemo(() => {
    if (!allowResubmit && adminView?.snapshot) {
      return extractEditableValues(adminView.live);
    }
    return extractSnapshotValues(adminView?.snapshot);
  }, [adminView?.snapshot, adminView?.live, allowResubmit]);

  const isWorkerRegistry = form.formCode.trim().toUpperCase() === 'FF001';
  const isHireHealthSurvey = form.formCode.trim().toUpperCase() === 'FF003';
  const isBankAccountDetails = form.formCode.trim().toUpperCase() === 'FF005';
  const isSiteArrivalConfirmation = form.formCode.trim().toUpperCase() === 'FF017';

  const fieldKeys = useMemo(() => {
    const keys = new Set<string>([
      ...Object.keys(fieldValues),
      ...Object.keys(snapshotFieldValues),
    ]);
    return Array.from(keys).sort();
  }, [fieldValues, snapshotFieldValues]);

  const handleSaveFields = async () => {
    if (!adminView?.live || !canAdminEdit) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = mergeEditableValues(adminView.live, fieldValues);
      const updated = await updateAdminFormSubmission(form.formCode, {
        email: employeeEmail.trim(),
        invitationId: invitationId.trim(),
        submissionRound: activeRound.submissionRound,
        payload,
      });
      setAdminView(updated);
      setFieldValues(extractEditableValues(updated.live));
      setMode('view');
      setPdfRefreshKey((k) => k + 1);
    } catch {
      setSaveError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleModeToggle = () => {
    if (mode === 'edit') {
      if (adminView?.live) setFieldValues(extractEditableValues(adminView.live));
      setMode('view');
      setSaveError(null);
    } else {
      setMode('edit');
      setPanelTab('fields');
    }
  };

  const handleDownload = () => {
    if (!previewBlobUrl) return;
    const empName = employeeName?.trim() || employeeEmail;
    const fileName = `${safeFileNamePart(empName)}_${safeFileNamePart(formTitle)}.pdf`;
    const a = document.createElement('a');
    a.href = previewBlobUrl;
    a.download = fileName;
    a.click();
  };

  const handleOpenInNewTab = () => {
    const url = previewApiUrl ?? previewBlobUrl;
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleApproveClick = () => {
    if (reviewDecision === false) {
      setReverseConfirm({
        message: 'この書類は却下済みです。承認しますか？',
        onConfirm: () => {
          setReverseConfirm(null);
          onApprove();
        },
      });
    } else {
      onApprove();
    }
  };

  const handleResubmitClick = () => {
    if (reviewDecision === true) {
      setReverseConfirm({
        message: 'この書類は承認済みです。再提出を依頼しますか？',
        onConfirm: () => {
          setReverseConfirm(null);
          onOpenResubmitModal();
        },
      });
    } else {
      onOpenResubmitModal();
    }
  };

  const auditBy = activeRound.adminCorrectedBy ?? adminView?.adminCorrectedBy;
  const auditAt = activeRound.adminCorrectedAt ?? adminView?.adminCorrectedAt;

  return (
    <div className="document-preview" role="dialog" aria-modal="true" aria-labelledby="filled-form-preview-title">
      <div className="document-preview__backdrop" />
      <div className="document-preview__panel">
        <header className="document-preview__header">
          <h2 id="filled-form-preview-title" className="document-preview__title">
            <span className="document-preview__title-doc">{formTitle}</span>{' '}
            <span
              className={
                allowResubmit
                  ? 'document-preview__title-round document-preview__title-round--latest'
                  : 'document-preview__title-round'
              }
            >
              {allowResubmit ? '提出物' : '前回の提出物'}（第{activeRound.submissionRound}ラウンド）
            </span>
          </h2>
          <div className="document-preview__header-actions">
            {canAdminEdit && adminLoadState === 'ready' && (
              <button type="button" className="profile-section-preview__mode-btn" onClick={handleModeToggle}>
                {mode === 'view' ? '編集' : '表示'}
              </button>
            )}
            {reviewDecision === false &&
              allowResubmit &&
              (!activeAdminComment || activeAdminComment.trim() === '') && (
                <img src={rejectIcon} alt="却下" className="document-preview__header-reject-icon" aria-hidden />
              )}
            <button
              type="button"
              className="document-preview__download-btn"
              aria-label="ダウンロード"
              onClick={handleDownload}
              disabled={!previewBlobUrl}
            >
              <img src={downloadAllIcon} alt="" className="document-preview__download-icon" aria-hidden />
              <span>ダウンロード</span>
            </button>
            <button
              type="button"
              className="document-preview__open-tab-btn"
              aria-label="新しいタブで開く"
              onClick={handleOpenInNewTab}
              disabled={previewLoading || (!previewApiUrl && !previewBlobUrl)}
            >
              新しいタブで開く
            </button>
            <button type="button" className="document-preview__close-btn" aria-label="閉じる" onClick={onClose}>
              ×
            </button>
          </div>
        </header>

        <div className="document-preview__body">
          <div className="filled-form-preview__tabs">
            <button
              type="button"
              className={panelTab === 'pdf' ? 'filled-form-preview__tab filled-form-preview__tab--active' : 'filled-form-preview__tab'}
              onClick={() => setPanelTab('pdf')}
            >
              PDF
            </button>
            <button
              type="button"
              className={panelTab === 'fields' ? 'filled-form-preview__tab filled-form-preview__tab--active' : 'filled-form-preview__tab'}
              onClick={() => setPanelTab('fields')}
            >
              入力内容
              {adminView?.snapshot && Object.keys(snapshotFieldValues).length > 0 ? '（比較）' : ''}
            </button>
          </div>

          {panelTab === 'pdf' && (
            <div className="document-preview__document-area">
              <span className="document-preview__pagination" aria-hidden>
                1 / 1
              </span>
              {previewLoading && <div className="document-preview__placeholder">読み込み中...</div>}
              {!previewLoading && (previewError || !previewBlobUrl) && (
                <div className="document-preview__placeholder">書類プレビューを表示できません</div>
              )}
              {!previewLoading && previewBlobUrl && (
                <PdfCanvasViewer
                  pdfUrl={previewBlobUrl}
                  title={formTitle}
                  className="document-preview__pdf-canvas"
                  renderErrorLabel="書類プレビューを表示できません"
                />
              )}
            </div>
          )}

          {panelTab === 'fields' && (
            <div className="document-preview__document-area profile-section-preview__scroll">
              {adminLoadState === 'loading' && <div className="document-preview__placeholder">読み込み中...</div>}
              {adminLoadState === 'error' && (
                <div className="document-preview__placeholder">入力内容を表示できません</div>
              )}
              {adminLoadState === 'ready' && (
                <>
                  {(auditBy || auditAt) && (
                    <div className="profile-section-preview__audit" style={{ margin: '12px 16px' }}>
                      管理者による修正: {auditBy || '—'}
                      {auditAt ? `（${auditAt.replace('T', ' ').slice(0, 16)}）` : ''}
                    </div>
                  )}
                  {adminView?.snapshot && !allowResubmit && (
                    <p className="profile-section-preview__muted" style={{ padding: '0 16px' }}>
                      提出時のスナップショットと現在の値の差分を表示しています。
                    </p>
                  )}
                  {!canAdminEdit && form.formCode.toUpperCase() === 'FF007' && (
                    <p className="profile-section-preview__muted" style={{ padding: '0 16px 8px' }}>
                      FF007 はスナップショット比較のみです。修正は従業員の再提出が必要です。
                    </p>
                  )}
                  {isWorkerRegistry ? (
                    <WorkerRegistryAdminFieldEditor
                      values={fieldValues}
                      editing={mode === 'edit' && canAdminEdit}
                      onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                      snapshotValues={
                        adminView?.snapshot && Object.keys(snapshotFieldValues).length > 0
                          ? snapshotFieldValues
                          : undefined
                      }
                    />
                  ) : isHireHealthSurvey ? (
                    <HireHealthSurveyAdminFieldEditor
                      values={fieldValues}
                      editing={mode === 'edit' && canAdminEdit}
                      onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                      snapshotValues={
                        adminView?.snapshot && Object.keys(snapshotFieldValues).length > 0
                          ? snapshotFieldValues
                          : undefined
                      }
                    />
                  ) : isBankAccountDetails ? (
                    <BankAccountDetailsAdminFieldEditor
                      values={fieldValues}
                      editing={mode === 'edit' && canAdminEdit}
                      onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                      snapshotValues={
                        adminView?.snapshot && Object.keys(snapshotFieldValues).length > 0
                          ? snapshotFieldValues
                          : undefined
                      }
                    />
                  ) : isSiteArrivalConfirmation ? (
                    <SiteArrivalConfirmationAdminFieldEditor
                      values={fieldValues}
                      editing={mode === 'edit' && canAdminEdit}
                      onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                      snapshotValues={
                        adminView?.snapshot && Object.keys(snapshotFieldValues).length > 0
                          ? snapshotFieldValues
                          : undefined
                      }
                    />
                  ) : (
                    <FormFieldEditor
                      fields={fieldKeys}
                      values={fieldValues}
                      editing={mode === 'edit' && canAdminEdit}
                      onChange={(key, value) => setFieldValues((prev) => ({ ...prev, [key]: value }))}
                      snapshotValues={
                        adminView?.snapshot && Object.keys(snapshotFieldValues).length > 0
                          ? snapshotFieldValues
                          : undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
          )}

          {saveError && <div className="profile-section-preview__save-error">{saveError}</div>}

          {reviewDecision === true && (
            <div className="document-preview__round-comment">
              <div className="document-preview__round-comment-inner document-preview__round-comment-inner--with-status-icon">
                <img src={approveIcon} alt="承認" className="document-preview__round-comment-approve-icon" aria-hidden />
                <div className="document-preview__round-comment-copy">
                  {activeAdminComment != null && activeAdminComment.trim() !== '' ? (
                    <>
                      <div className="document-preview__round-comment-title">コメント</div>
                      <div className="document-preview__round-comment-text">{activeAdminComment}</div>
                    </>
                  ) : (
                    <div className="document-preview__round-comment-title">承認</div>
                  )}
                </div>
              </div>
            </div>
          )}
          {reviewDecision === false && activeAdminComment != null && activeAdminComment.trim() !== '' && (
            <div className="document-preview__round-comment">
              <div className="document-preview__round-comment-inner document-preview__round-comment-inner--with-status-icon">
                <img src={rejectIcon} alt="却下" className="document-preview__round-comment-reject-icon" aria-hidden />
                <div className="document-preview__round-comment-copy">
                  <div className="document-preview__round-comment-title">コメント</div>
                  <div className="document-preview__round-comment-text">{activeAdminComment}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!readOnly && (
        <footer className="document-preview__footer">
          <div className="document-preview__footer-actions">
            {mode === 'edit' && canAdminEdit && (
              <button
                type="button"
                className="document-preview__approve-btn"
                disabled={saving}
                onClick={() => void handleSaveFields()}
              >
                {saving ? '保存中...' : '保存'}
              </button>
            )}
            {(reviewDecision === null || reviewDecision === true) && (
              <button type="button" className="document-preview__resubmit-btn" onClick={handleResubmitClick}>
                再提出を依頼する
              </button>
            )}
            {(reviewDecision === null || reviewDecision === false) && (
              <button type="button" className="document-preview__approve-btn" onClick={handleApproveClick}>
                承認する
              </button>
            )}
          </div>
        </footer>
        )}
      </div>

      <ResubmitCommentModal
        open={showResubmitModal}
        reason={resubmitReason}
        onReasonChange={onResubmitReasonChange}
        onClose={onCloseResubmitModal}
        onSave={onSaveResubmit}
      />

      {reverseConfirm && (
        <div className="document-preview__reverse-confirm" role="dialog" aria-modal="true" aria-labelledby="reverse-confirm-title">
          <div className="document-preview__reverse-confirm-backdrop" onClick={() => setReverseConfirm(null)} aria-hidden />
          <div className="document-preview__reverse-confirm-box">
            <p id="reverse-confirm-title" className="document-preview__reverse-confirm-message">
              {reverseConfirm.message}
            </p>
            <div className="document-preview__reverse-confirm-actions">
              <button type="button" className="document-preview__reverse-confirm-cancel" onClick={() => setReverseConfirm(null)}>
                キャンセル
              </button>
              <button type="button" className="document-preview__reverse-confirm-ok" onClick={reverseConfirm.onConfirm}>
                はい
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilledFormPreview;
